import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Gift, Plus, X, ChevronRight, Ban, Search, Download } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { formatCurrency, formatDateTime } from '../lib/utils'

type GiftCardStatus = 'active' | 'exhausted' | 'expired' | 'voided' | 'refunded'
type GiftCardSource = 'sold' | 'promo'

interface GiftCard {
  id: string
  code: string
  original_value_fils: number
  current_balance_fils: number
  status: GiftCardStatus
  source: GiftCardSource
  issued_at: string
  expires_at: string
  recipient_phone: string | null
  recipient_email: string | null
  recipient_note: string | null
  issued_by_cashier_id: string | null
  issued_by_admin_id: string | null
  issued_by_cashier_name: string | null
  issued_by_admin_name: string | null
}

interface GiftCardTxn {
  id: string
  type: 'issuance' | 'spend' | 'refund_addback' | 'void' | 'expiry'
  amount_fils: number
  order_id: string | null
  pos_return_id: string | null
  actor_cashier_name: string | null
  actor_admin_name: string | null
  description: string | null
  created_at: string
}

const STATUS_LABELS: Record<GiftCardStatus, string> = {
  active: 'Active',
  exhausted: 'Exhausted',
  expired: 'Expired',
  voided: 'Voided',
  refunded: 'Refunded',
}
const STATUS_COLORS: Record<GiftCardStatus, string> = {
  active: 'bg-green-500/15 text-green-400',
  exhausted: 'bg-gray-500/15 text-gray-400',
  expired: 'bg-yellow-500/15 text-yellow-400',
  voided: 'bg-red-500/15 text-red-400',
  refunded: 'bg-blue-500/15 text-blue-400',
}

export default function GiftCards() {
  const { can } = useAuth()
  // Issuance + void allowed for super_admin / operations / accounting —
  // mirrors the worker's requireRole on POST /admin/gift-cards.
  const canManage = can(['super_admin', 'operations', 'accounting'])
  const qc = useQueryClient()
  const [status, setStatus] = useState<GiftCardStatus | ''>('')
  const [source, setSource] = useState<GiftCardSource | ''>('')
  const [q, setQ] = useState('')
  const [showIssue, setShowIssue] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)

  const query = new URLSearchParams()
  if (status) query.set('status', status)
  if (source) query.set('source', source)
  if (q.trim()) query.set('q', q.trim())

  const { data, isLoading } = useQuery<{ gift_cards: GiftCard[]; total: number }>({
    queryKey: ['gift-cards', status, source, q],
    queryFn: () => api.get(`/gift-cards?${query.toString()}`).then(r => r.data),
  })

  const refresh = () => qc.invalidateQueries({ queryKey: ['gift-cards'] })

  async function downloadCsv(path: string, filename: string) {
    try {
      const res = await api.get(path, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Download failed'
      alert(msg)
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Gift size={20} className="text-[#c41e3a]" />
            Gift Cards
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Bearer instrument — anyone with the code can redeem. Sold at the till or issued as a promo.
            Refunds of gift-card-paid sales route to store credit only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const date = new Date().toISOString().slice(0, 10)
              const suffix = query.toString() ? `?${query.toString()}` : ''
              downloadCsv(`/gift-cards/export.csv${suffix}`, `gift-cards-${date}.csv`)
            }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 text-gray-300 text-sm hover:bg-white/5"
          >
            <Download size={14} />
            Export CSV
          </button>
          {canManage && (
            <button
              onClick={() => setShowIssue(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]"
            >
              <Plus size={16} />
              Issue card
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search code, phone or email"
            className="pl-9 pr-3 py-2 w-64 bg-[#1a1a1a] border border-white/10 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#c41e3a]"
          />
        </div>
        <select
          value={status}
          onChange={e => setStatus(e.target.value as GiftCardStatus | '')}
          className="px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#c41e3a]"
        >
          <option value="">All statuses</option>
          {(Object.keys(STATUS_LABELS) as GiftCardStatus[]).map(s => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
        <select
          value={source}
          onChange={e => setSource(e.target.value as GiftCardSource | '')}
          className="px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#c41e3a]"
        >
          <option value="">All sources</option>
          <option value="sold">Sold</option>
          <option value="promo">Promo</option>
        </select>
        <span className="ml-auto text-xs text-gray-500">
          {data ? `${data.total} card${data.total === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (data?.gift_cards ?? []).length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No gift cards match the current filters.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Code</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-right px-5 py-3 font-medium">Balance</th>
                <th className="text-right px-5 py-3 font-medium">Original</th>
                <th className="text-left px-5 py-3 font-medium">Source</th>
                <th className="text-left px-5 py-3 font-medium">Recipient</th>
                <th className="text-left px-5 py-3 font-medium">Issued</th>
                <th className="text-left px-5 py-3 font-medium">Expires</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {(data?.gift_cards ?? []).map(gc => (
                <tr
                  key={gc.id}
                  onClick={() => setDetailId(gc.id)}
                  className="border-t border-white/10 hover:bg-white/5 cursor-pointer"
                >
                  <td className="px-5 py-3 text-sm font-mono text-white">{gc.code}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[gc.status]}`}>
                      {STATUS_LABELS[gc.status]}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm text-right text-white">{formatCurrency(gc.current_balance_fils)}</td>
                  <td className="px-5 py-3 text-sm text-right text-gray-400">{formatCurrency(gc.original_value_fils)}</td>
                  <td className="px-5 py-3 text-xs text-gray-400 capitalize">{gc.source}</td>
                  <td className="px-5 py-3 text-xs text-gray-400">
                    {gc.recipient_phone || gc.recipient_email || <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-5 py-3 text-xs text-gray-500">{formatDateTime(gc.issued_at)}</td>
                  <td className="px-5 py-3 text-xs text-gray-500">{formatDateTime(gc.expires_at)}</td>
                  <td className="px-2"><ChevronRight size={14} className="text-gray-600" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showIssue && (
        <IssueGiftCardModal
          onClose={() => setShowIssue(false)}
          onIssued={() => { setShowIssue(false); refresh() }}
        />
      )}

      {detailId && (
        <GiftCardDetail
          id={detailId}
          canManage={canManage}
          onClose={() => setDetailId(null)}
          onVoided={() => { refresh() }}
        />
      )}
    </div>
  )
}

function IssueGiftCardModal({
  onClose, onIssued,
}: { onClose: () => void; onIssued: () => void }) {
  const [valueJd, setValueJd] = useState('')
  const [source, setSource] = useState<GiftCardSource>('promo')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [note, setNote] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const v = parseFloat(valueJd)
    if (!Number.isFinite(v) || v <= 0) { setError('Enter a positive value in JD'); return }
    setSubmitting(true)
    try {
      await api.post('/gift-cards', {
        value_fils: Math.round(v * 1000),
        source,
        recipient_phone: phone.trim() || undefined,
        recipient_email: email.trim() || undefined,
        recipient_note: note.trim() || undefined,
        description: description.trim() || undefined,
      })
      onIssued()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? "Couldn't issue card.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <form onSubmit={onSubmit} className="w-full max-w-md bg-[#1a1a1a] border border-white/10 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-sm font-semibold">Issue gift card</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500">Value (JD)</label>
            <input
              type="number" min="0" step="0.001"
              value={valueJd}
              onChange={e => setValueJd(e.target.value)}
              placeholder="e.g. 50"
              className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">Source</label>
            <select
              value={source}
              onChange={e => setSource(e.target.value as GiftCardSource)}
              className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]"
            >
              <option value="promo">Promo (no payment recorded)</option>
              <option value="sold">Sold (over-the-counter)</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-500">Recipient phone (optional)</label>
          <input
            type="text" inputMode="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="07XXXXXXXX"
            className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm font-mono text-white focus:outline-none focus:border-[#c41e3a]"
          />
          <p className="text-xs text-gray-600 mt-1">SMS with the redeem link will be sent if a phone is entered.</p>
        </div>

        <div>
          <label className="text-xs text-gray-500">Recipient email (optional)</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="recipient@example.com"
            className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]"
          />
        </div>

        <div>
          <label className="text-xs text-gray-500">Gift message (optional, shown to recipient)</label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            placeholder="Happy birthday from the team!"
            className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]"
          />
        </div>

        <div>
          <label className="text-xs text-gray-500">Internal note (audit / ledger)</label>
          <input
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="e.g. Influencer collab — June 2026"
            className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]"
          />
        </div>

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 text-sm py-2 rounded-lg bg-[#c41e3a] text-white font-medium hover:bg-[#a01830] disabled:opacity-50"
          >
            {submitting ? 'Issuing…' : 'Issue card'}
          </button>
        </div>
      </form>
    </div>
  )
}

function GiftCardDetail({
  id, canManage, onClose, onVoided,
}: {
  id: string
  canManage: boolean
  onClose: () => void
  onVoided: () => void
}) {
  const { data, isLoading } = useQuery<{ gift_card: GiftCard; transactions: GiftCardTxn[] }>({
    queryKey: ['gift-card', id],
    queryFn: () => api.get(`/gift-cards/${id}`).then(r => r.data),
  })
  const qc = useQueryClient()
  const [voidOpen, setVoidOpen] = useState(false)

  const card = data?.gift_card
  const txns = data?.transactions ?? []
  const canVoid = canManage && card && card.status !== 'voided' && card.status !== 'refunded'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-2xl bg-[#1a1a1a] border border-white/10 rounded-xl p-5 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-sm font-semibold flex items-center gap-2">
            <Gift size={16} className="text-[#c41e3a]" />
            Gift card detail
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={16} />
          </button>
        </div>

        {isLoading || !card ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (
          <>
            <div className="bg-[#242424] border border-[#3a3a3a] rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-mono text-lg text-white tracking-widest">{card.code}</div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[card.status]}`}>
                  {STATUS_LABELS[card.status]}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs text-gray-400">
                <div>
                  <div className="text-gray-600">Balance</div>
                  <div className="text-white text-sm font-medium">{formatCurrency(card.current_balance_fils)}</div>
                </div>
                <div>
                  <div className="text-gray-600">Original value</div>
                  <div className="text-white text-sm font-medium">{formatCurrency(card.original_value_fils)}</div>
                </div>
                <div>
                  <div className="text-gray-600">Source</div>
                  <div className="text-white capitalize">{card.source}</div>
                </div>
                <div>
                  <div className="text-gray-600">Expires</div>
                  <div className="text-white">{formatDateTime(card.expires_at)}</div>
                </div>
                <div>
                  <div className="text-gray-600">Issued by</div>
                  <div className="text-white">
                    {card.issued_by_admin_name ?? card.issued_by_cashier_name ?? '—'}
                    {card.issued_by_admin_id ? ' (admin)' : card.issued_by_cashier_id ? ' (cashier)' : ''}
                  </div>
                </div>
                <div>
                  <div className="text-gray-600">Issued at</div>
                  <div className="text-white">{formatDateTime(card.issued_at)}</div>
                </div>
                <div>
                  <div className="text-gray-600">Recipient phone</div>
                  <div className="text-white font-mono">{card.recipient_phone ?? '—'}</div>
                </div>
                <div>
                  <div className="text-gray-600">Recipient email</div>
                  <div className="text-white">{card.recipient_email ?? '—'}</div>
                </div>
              </div>
              {card.recipient_note && (
                <div className="text-xs">
                  <div className="text-gray-600">Gift message</div>
                  <div className="text-white italic">"{card.recipient_note}"</div>
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs uppercase tracking-wide text-gray-500">Ledger</h4>
                {txns.length > 0 && (
                  <button
                    onClick={async () => {
                      try {
                        const res = await api.get(`/gift-cards/${id}/ledger.csv`, { responseType: 'blob' })
                        const url = URL.createObjectURL(res.data)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = `gift-card-${card.code}-ledger.csv`
                        document.body.appendChild(a); a.click(); a.remove()
                        URL.revokeObjectURL(url)
                      } catch (e) {
                        alert((e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Download failed')
                      }
                    }}
                    className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white"
                  >
                    <Download size={12} />
                    Export ledger
                  </button>
                )}
              </div>
              <div className="bg-[#242424] border border-[#3a3a3a] rounded-lg overflow-hidden">
                {txns.length === 0 ? (
                  <div className="p-4 text-center text-xs text-gray-500">No transactions.</div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-white/5 text-gray-500">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">When</th>
                        <th className="text-left px-3 py-2 font-medium">Type</th>
                        <th className="text-right px-3 py-2 font-medium">Amount</th>
                        <th className="text-left px-3 py-2 font-medium">Actor</th>
                        <th className="text-left px-3 py-2 font-medium">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {txns.map(t => (
                        <tr key={t.id} className="border-t border-white/10">
                          <td className="px-3 py-2 text-gray-400">{formatDateTime(t.created_at)}</td>
                          <td className="px-3 py-2 text-gray-300 capitalize">{t.type.replace('_', ' ')}</td>
                          <td className={`px-3 py-2 text-right font-mono ${t.amount_fils >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {t.amount_fils >= 0 ? '+' : ''}{formatCurrency(t.amount_fils)}
                          </td>
                          <td className="px-3 py-2 text-gray-400">
                            {t.actor_admin_name ?? t.actor_cashier_name ?? <span className="text-gray-600">system</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-500">{t.description ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]"
              >
                Close
              </button>
              {canVoid && (
                <button
                  onClick={() => setVoidOpen(true)}
                  className="flex items-center justify-center gap-1.5 text-sm py-2 px-4 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700"
                >
                  <Ban size={14} />
                  Void card
                </button>
              )}
            </div>
          </>
        )}

        {voidOpen && card && (
          <VoidGiftCardModal
            card={card}
            onClose={() => setVoidOpen(false)}
            onVoided={() => {
              setVoidOpen(false)
              qc.invalidateQueries({ queryKey: ['gift-card', id] })
              onVoided()
            }}
          />
        )}
      </div>
    </div>
  )
}

function VoidGiftCardModal({
  card, onClose, onVoided,
}: { card: GiftCard; onClose: () => void; onVoided: () => void }) {
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!reason.trim()) { setError('A reason is required for the audit trail'); return }
    setSubmitting(true)
    try {
      await api.post(`/gift-cards/${card.id}/void`, { reason: reason.trim() })
      onVoided()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? "Couldn't void card.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
      <form onSubmit={onSubmit} className="w-full max-w-sm bg-[#1a1a1a] border border-white/10 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-sm font-semibold">Void {card.code}?</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={16} />
          </button>
        </div>
        <p className="text-xs text-gray-400 leading-relaxed">
          This zeroes the remaining {formatCurrency(card.current_balance_fils)} and records a void in the ledger.
          Use refund-on-return at the cashier till if the buyer wants the value back as store credit.
        </p>
        <div>
          <label className="text-xs text-gray-500">Reason</label>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. Card lost, recipient never claimed"
            className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]"
            autoFocus
          />
        </div>
        {error && <p className="text-xs text-red-400">{error}</p>}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 text-sm py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? 'Voiding…' : 'Void card'}
          </button>
        </div>
      </form>
    </div>
  )
}
